import { Box, Typography, visuallyHidden } from "@outschool/backpack";
import { ListingCard_ListingFragment } from "@outschool/gql-frontend-generated";
import { faChevronLeft } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { createPathWithActivity } from "@outschool/routes";
import { useLogSearchImpression } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import {
  AvatarImage,
  DEFAULT_AVATAR,
  TrackedButton
} from "@outschool/ui-components-shared";
import {
  Carousel,
  IconButton,
  Loading,
  Space
} from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React, { useCallback, useState } from "react";

import {
  ListRenderableCard,
  RenderableListingsCard
} from "../../../../shared/parentDashboard/CardDefinition";
import {
  TitleType,
  useCardTitleTranslation
} from "../../../../shared/parentDashboard/cardTypes/useCardTitleTranslation";
import Link from "../../Link";
import StickyMobileHeader from "../../nav/StickyMobileHeader";
import DatePicker from "../DatePicker";
import { useParentDashboardFilters } from "../ParentDashboardFilters";
import ShowMore from "../ShowMore";
import useListingsForCard from "../useListingsForCard";
import Listing from "./Listing";

function ClassList({ listings }: { listings: ListingCard_ListingFragment[] }) {
  return (
    <Box
      sx={{
        listStyle: "none"
      }}
    >
      <ul style={{ listStyle: "none" }}>
        {listings.map((listing, index) => (
          <li key={listing.activity.uid || index}>
            <ClassListListing
              listing={listing}
              listingIndex={index}
              numListings={listings.length}
            />
          </li>
        ))}
      </ul>
    </Box>
  );
}

function ClassListListing({ listing, listingIndex, numListings }: $TSFixMe) {
  // impression refs are handled inside Listing
  const { searchImpressionRef, trackSearchImpressionClick } =
    useLogSearchImpression({
      listing,
      listingIndex,
      pageLocation: "PARENT_DASHBOARD"
    });
  return (
    <Box ref={searchImpressionRef}>
      <Link
        to={createPathWithActivity(listing.activity)}
        trackingName="Activity Impression"
        trackingViewEventName="Activity Impression"
        trackingTouchEventName="Activity Impression Click"
        trackingUniqueId={listing.activity.uid}
        sharedProperties={{
          insertion_id: listing.insertionId,
          activity_uid: listing.activity.uid,
          page_name: "parent_dashboard_listings",
          order: listingIndex + 1,
          numListings
        }}
        useLinkTracking
        onClick={trackSearchImpressionClick}
        trackViews={true}
      >
        <Listing listing={listing} showSaved />
        {listingIndex + 1 !== numListings && (
          <hr style={{ marginTop: "28px", marginBottom: "28px" }} />
        )}
      </Link>
    </Box>
  );
}

function BackButton(props: $TSFixMe) {
  const { t } = useTranslation(
    "client\\components\\parentDashboard\\lists\\BasicList"
  );
  return (
    <IconButton
      icon={faChevronLeft}
      screenReaderLabel={t("Go back to dashboard")}
      onClick={() => window.history.back()}
      {...props}
    />
  );
}

function StickyHeader({ title }: $TSFixMe) {
  return (
    <Box
      flex
      sx={{
        alignItems: "center"
      }}
    >
      <BackButton />
      <Typography
        sx={{
          padding: "0.25em",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: "1px",
          WebkitBoxOrient: "vertical"
        }}
        variant="h4"
      >
        {title}
      </Typography>
    </Box>
  );
}

function Learner({ child, isSelected, toggleLearner }: $TSFixMe) {
  const label = child.age ? `${child.name} (${child.age})` : child.name;
  return (
    <Box>
      <TrackedButton
        size="small"
        sx={{
          borderColor: isSelected ? "#6161F2 !important" : "#C2C2C2 !important",
          borderRadius: "20px",
          boxShadow: "none",
          marginRight: "8px",
          backgroundColor: isSelected
            ? "#CCD4FF !important"
            : "white !important",
          fontSize: "16px"
        }}
        onClick={() => toggleLearner(child)}
        trackingName="parent_dashboard_list"
        trackingUniqueId={"learner_button"}
      >
        <Box
          flex
          sx={{
            whiteSpace: "nowrap",
            alignItems: "center",
            color: "grey.900"
          }}
        >
          <AvatarImage
            avatar={child.avatar || DEFAULT_AVATAR}
            height={24}
            name={child.name}
            sx={{ paddingRight: "tiny" }}
          />
          <Box
            sx={{
              marginBottom: "2px"
            }}
          >
            {label}
          </Box>
        </Box>
      </TrackedButton>
    </Box>
  );
}

function LearnerFilter({ localFilters, setLocalFilters }: $TSFixMe) {
  const { t } = useTranslation(
    "client\\components\\parentDashboard\\lists\\BasicList"
  );
  const { currentUser } = useSession();
  const [selectedLearnerUid, setSelectedLearnerUid] = useState<string>();

  const toggleLearner = useCallback(
    (learner: $TSFixMe) => {
      if (selectedLearnerUid === learner.uid) {
        // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        setSelectedLearnerUid(null);
        setLocalFilters(_.omit(localFilters, ["age"]));
      } else {
        setSelectedLearnerUid(learner.uid);
        setLocalFilters({ ...localFilters, age: learner.age.toString() });
      }
    },
    [selectedLearnerUid, setSelectedLearnerUid, localFilters, setLocalFilters]
  );

  // @ts-ignore TS(2532): Object is possibly 'undefined'.
  if (!(currentUser?.children.length > 1)) {
    return null;
  }
  // @ts-ignore TS(2531): Object is possibly 'null'.
  const children = _.sortBy(currentUser.children, c => c.name);
  return (
    <Box
      flex
      sx={{
        width: "100%"
      }}
    >
      <span style={visuallyHidden}>{t("Filter by learner:")}</span>
      <Carousel
        sx={{
          paddingTop: "12px",
          maxWidth: "100%"
        }}
      >
        <Box flex>
          <Space x="24px"></Space>
          {children.map(child => (
            <Learner
              key={child.uid}
              child={child}
              isSelected={selectedLearnerUid === child.uid}
              toggleLearner={toggleLearner}
            />
          ))}
          <Space x="24px"></Space>
        </Box>
      </Carousel>
    </Box>
  );
}

function NoResults({ url }: $TSFixMe) {
  const { t } = useTranslation(
    "client\\components\\parentDashboard\\lists\\BasicList"
  );
  return (
    <Box
      sx={{
        padding: "1em",
        textAlign: "center"
      }}
    >
      <Typography variant="inherit">
        {t("No upcoming classes found.")}
      </Typography>
      {url && (
        <ShowMore
          url={url}
          trackingName="parent dashboard no results"
          text={t("Expand your search")}
        />
      )}
    </Box>
  );
}

export default function Listings({ card }: { card: ListRenderableCard }) {
  const { t } = useTranslation(
    "client\\components\\parentDashboard\\lists\\BasicList"
  );
  const title = useCardTitleTranslation(card.title as TitleType);

  const [learnerFilters, setLearnerFilters] = useState({});
  const { searchFilters } = useParentDashboardFilters();
  const localFilters = React.useMemo(
    () => ({ ...searchFilters, ...learnerFilters }),
    [searchFilters, learnerFilters]
  );

  const { loading, listings, showMoreUrl } = useListingsForCard(
    card,
    false,
    localFilters
  );

  const [topNode, setTopNode] = useState(null);
  const showMoreText = showMoreUrl
    ? (card as RenderableListingsCard).showMoreText || t("Show more classes")
    : null;
  return (
    <Box
      sx={{
        width: "100%"
      }}
    >
      <StickyMobileHeader
        leftChildren={<StickyHeader title={card.title} />}
        watchNode={topNode}
      />
      <Box ref={setTopNode} />
      <BackButton
        sx={{ margin: "medium", marginBottom: "0", marginLeft: "8px" }}
      />
      <Typography
        variant="h3"
        sx={{
          fontWeight: "fontWeightBold",
          paddingLeft: "24px",
          paddingRight: "1em"
        }}
      >
        {title}
      </Typography>
      <LearnerFilter
        localFilters={learnerFilters}
        setLocalFilters={setLearnerFilters}
      />
      <Box
        sx={{
          paddingTop: "12px"
        }}
      >
        <DatePicker />
      </Box>
      <Box
        sx={{
          padding: "32px 24px"
        }}
      >
        {loading ? (
          <Loading sx={{ padding: "small" }} />
        ) : !(listings?.length > 0) ? (
          <NoResults url={showMoreUrl} />
        ) : (
          <Box>
            <ClassList listings={listings} />
            {showMoreUrl && (
              <>
                <hr style={{ marginTop: "28px", marginBottom: "28px" }} />
                <ShowMore
                  url={showMoreUrl}
                  trackingName="parent dashboard show more"
                  text={showMoreText}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
